import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useCurrentLang from '@shared/hooks/useCurrentLang';
import { ServiceProcessFieldTableColumnVM } from '@shared/types/process';
import { TableFieldFilterRow } from './TableFieldFilterRow';
import { TableFieldParamsProvider } from './TableFieldParamsProvider';
import {
    buildTableFieldFilters,
    queryStateToParams,
    syncParamsToQueryState,
} from './tableFieldFilterUtils';
import { TableRowsQueryState } from './tableFieldUtils';

type Props = {
    columns: ServiceProcessFieldTableColumnVM[];
    queryState: TableRowsQueryState;
    onQueryStateChange: (
        updater: (prev: TableRowsQueryState) => TableRowsQueryState,
    ) => void;
};

export function TableFieldFilterBar({
    columns,
    queryState,
    onQueryStateChange,
}: Props) {
    const { t } = useTranslation();
    const lang = useCurrentLang();

    const columnIds = useMemo(
        () => columns.map((column) => column.id),
        [columns],
    );

    const filters = useMemo(
        () =>
            buildTableFieldFilters(
                columns,
                lang,
                t('translation:search'),
            ),
        [columns, lang, t],
    );

    const initialParams = useMemo(
        () => queryStateToParams(queryState, columnIds),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- seed store once per mount
        [],
    );

    const handleParamsChange = useCallback(
        (params: Record<string, unknown>) => {
            const synced = syncParamsToQueryState(params, columnIds);

            onQueryStateChange((prev) => ({
                ...prev,
                ...synced,
            }));
        },
        [columnIds, onQueryStateChange],
    );

    if (!columns.length) {
        return null;
    }

    return (
        <TableFieldParamsProvider
            initialParams={initialParams}
            onParamsChange={handleParamsChange}
        >
            <TableFieldFilterRow filters={filters} />
        </TableFieldParamsProvider>
    );
}
